/**
 * install-route — what "install this app" can actually MEAN for the visitor in
 * front of us, and the only place in the library that reasons about which
 * browser it is running in.
 *
 * Every platform that runs a web app can install one. Only Chromium hands the
 * page an API for it; everywhere else the install is a menu item the page can
 * describe and cannot perform, and the menu item is a DIFFERENT one on each
 * platform. Classifying into "Chromium or not" therefore leaves a macOS Safari
 * visitor, a Firefox Android visitor and an iPad visitor all in the same
 * "unavailable" bucket while all three are one gesture away from an installed
 * app — and conflating iOS's Add to Home Screen with macOS's Add to Dock (they
 * are different words, in different menus, on different OS versions) is the
 * defect that classification produces the moment it stops giving up.
 *
 * VERIFIED 2026-09-08 against each platform's own authority; the summary the
 * component's reference page carries is this table.
 *
 *   Chromium desktop + Android      beforeinstallprompt → prompt()      API
 *     (Chrome, Edge, Opera, Samsung Internet)
 *     — MDN browser-compat-data 8.0.14: chrome 44, chrome_android 44,
 *       edge 79, opera 31, samsunginternet 5.0; firefox/safari all `false`.
 *   iOS / iPadOS, EVERY browser     Share → Add to Home Screen          none
 *     — MDN "Making PWAs installable": on iOS 16.4+ the Share menu installs
 *       from Safari, Chrome, Edge, Firefox and Orion alike, because the share
 *       sheet is the system's, not the browser's.
 *   macOS Safari, Sonoma 14+        File → Add to Dock                  none
 *     — Apple HT104996: "Requires macOS Sonoma 14 or later … choose File >
 *       Add to Dock. Or click the Share button …, then choose Add to Dock."
 *       webkit.org's Safari 17.0 notes say the same. NOT iOS's wording and
 *       not iOS's menu.
 *   Android, no WebAPK              menu → Add to Home screen           none
 *     (Firefox, Opera, Chrome without Google Mobile Services)
 *     — MDN, same page: those browsers "add a browser-badged home-screen
 *       shortcut that opens the site in the browser". The label is Firefox
 *       for Android's own `browser_menu_add_to_homescreen`.
 *   Firefox desktop                 —                                   none
 *     — Firefox 143 (2025-09-16) pins websites to the Windows taskbar, and
 *       Firefox has no manifest-driven install at all. Windows only, absent
 *       from Microsoft Store builds, and nothing about any of that is
 *       observable from a page — so this platform is told nothing, which is
 *       the honest answer rather than the missing one.
 *   Already installed, anywhere     —                                   —
 *     — `display-mode` media query (Safari 13 / iOS 12.2 / Firefox 47 /
 *       Chrome 42), `navigator.standalone`, `getInstalledRelatedApps()`.
 *
 * NAGWARE is what this file exists to make hard. On every platform without an
 * API the "can install" fact is a PROPERTY OF THE PLATFORM, true on this load
 * and on every load for the rest of the install's life — so anything that
 * raises itself off that fact raises itself again next week, and the week
 * after. Only `browser-prompt` is an EVENT: the browser decided, at this
 * moment, that this visitor should be offered an install, and the deferred
 * event is spent the first time it is used. `<schmancy-install-app>` is what
 * enforces the distinction — it will only float itself for the event-backed
 * route, and every standing route renders exactly where the consumer put it.
 */
import { state } from '@schmancy/state'
import {
	catchError,
	distinctUntilChanged,
	EMPTY,
	exhaustMap,
	filter,
	from,
	fromEvent,
	map,
	merge,
	type Observable,
	of,
	shareReplay,
	startWith,
	Subject,
	switchMap,
	takeUntil,
	tap,
	timer,
} from 'rxjs'

declare global {
	/** Chromium-only, non-standard: the deferred offer `prompt()` raises. */
	interface BeforeInstallPromptEvent extends Event {
		prompt(): Promise<void>
		readonly userChoice: Promise<{ readonly outcome: 'accepted' | 'dismissed' }>
	}
	interface Navigator {
		/** WebKit on Apple platforms; true only when the page IS the installed app. */
		readonly standalone?: boolean
		/** Chromium 84+. Rejects outside a top-level secure context. */
		getInstalledRelatedApps?(): Promise<readonly { platform?: string; id?: string; url?: string }[]>
	}
}

/**
 * The one install this visitor can perform, right now.
 *
 * - `installed`      — running as the installed app, or installed while open.
 * - `browser-prompt` — a deferred `beforeinstallprompt` is held;
 *                      `requestInstall()` raises the browser's own dialog.
 * - `ios-share`      — iPhone/iPad: Share → Add to Home Screen.
 * - `macos-dock`     — macOS Safari: File → Add to Dock. Deliberately NOT the
 *                      same value as `ios-share`; different words, different
 *                      menu, and showing one platform's to the other is the
 *                      single most common defect in this category.
 * - `menu-shortcut`  — an Android browser that installs no WebAPK: browser
 *                      menu → Add to Home screen.
 * - `unavailable`    — nothing this visitor can act on, or the offer was
 *                      already answered this session. Render nothing: an
 *                      instruction nobody can follow is worse than silence.
 */
export type InstallRoute = 'installed' | 'browser-prompt' | 'ios-share' | 'macos-dock' | 'menu-shortcut' | 'unavailable'

/** The routes that have something to say. `installRoute` may hold any of the six. */
export type OfferedInstallRoute = Exclude<InstallRoute, 'installed' | 'unavailable'>

/**
 * Reactive: read `installRoute.value` inside a `SchmancyElement`'s `render()`
 * and the component re-renders when the classification moves; `installRoute.$`
 * is the same fact as a stream. THIS is the seam a consuming app binds to —
 * along with `requestInstall()` below — and it is the whole seam.
 *
 * `keepOnClear`: which install a browser offers is a fact about the device,
 * not about whoever is signed in, so `clearStates()` on an identity change
 * leaves it alone.
 */
export const installRoute = state<InstallRoute>('schmancy/install-route').memory('unavailable', { keepOnClear: true })

/**
 * PLATFORM PROBES — the only browser reasoning in the library, all of it here.
 *
 * Every one is an INTERFACE or a MEDIA probe. No user-agent string is parsed,
 * because every string that would answer these questions lies: iPadOS reports
 * a macOS user agent by default (which is exactly how an iPad gets shown a
 * File menu it does not have), Safari's macOS version has been frozen at
 * `10_15_7` for years, and Chromium's `userAgentData` is absent from the two
 * engines this needs to tell apart.
 *
 * Where no capability test exists, that is said rather than papered over.
 */

/**
 * WebKit on an Apple platform — Safari, and every WKWebView browser on iOS,
 * iPadOS and macOS.
 *
 * There is no capability that means "this browser can Add to Home Screen", so
 * the probe is for the interface WebKit publishes on Apple platforms and
 * nowhere else. `standalone` is WebKit's own answer to "am I the installed
 * app"; its PRESENCE is what identifies the engine, its VALUE is read
 * separately below. WebKit gates it on `ENABLE(NAVIGATOR_STANDALONE)`, which
 * `PlatformEnableCocoa.h` turns on for every Cocoa platform with no
 * `PLATFORM(IOS_FAMILY)` guard — so macOS Safari carries it too, and the
 * touch probe below is what separates the two rather than this one.
 *
 * If a future WebKit withdrew it, both Apple branches would go quiet and every
 * Apple visitor would fall to `unavailable` — no offer, never a wrong one.
 */
const appleWebKit = 'standalone' in navigator

/**
 * iPhone / iPad, told apart from a Mac by a capability rather than by the user
 * agent that deliberately hides the difference.
 *
 * `Navigator::maxTouchPoints` in WebKit returns 5 under
 * `ENABLE(IOS_TOUCH_EVENTS) && !PLATFORM(MACCATALYST)` and 0 otherwise — so on
 * an Apple platform a positive count IS "iOS family", and a Mac (touch bar,
 * trackpad, touchscreen display or none) reports 0. Two site-specific WebKit
 * quirks can force 0 on iOS; a site on that list would be offered nothing
 * instead of the wrong thing.
 */
const iosFamily = appleWebKit && navigator.maxTouchPoints > 0

/**
 * A Safari-17-era interface, standing in for a capability that does not exist:
 * nothing on the page can ask whether the File menu has Add to Dock.
 *
 * Add to Dock shipped in Safari 17.0 and requires macOS Sonoma 14 (Apple
 * HT104996). Safari 17 and 18 also shipped for Ventura and Monterey, where the
 * menu item is absent, and the macOS VERSION is not observable from a page at
 * all — the user agent is frozen and Safari implements no UA-CH. So this probe
 * cuts off Safari 16 and earlier, and the one thing it cannot see is a Ventura
 * or Monterey Mac on Safari 17/18, which is told about a menu item it does not
 * have. That population only shrinks; the alternative — pinning to a
 * Sonoma-only Safari release — would go quiet for every Mac that has the
 * feature but not the newest Safari, which is a larger set and a worse trade.
 */
const safari17Era = 'popover' in HTMLElement.prototype

/**
 * Every `display-mode` that means "this page is the installed app". A manifest
 * is free to ask for any of them, so testing `standalone` alone reports a
 * `minimal-ui` or `fullscreen` install as not installed and offers to install
 * it again. An engine that does not know `window-controls-overlay` reads that
 * one query as false and the rest of the list still answers.
 */
const INSTALLED_DISPLAY_MODES =
	'(display-mode: standalone), (display-mode: minimal-ui), (display-mode: fullscreen), (display-mode: window-controls-overlay)'

/**
 * How long the standing Android answer waits for Chromium's own offer.
 *
 * `beforeinstallprompt` arrives a beat after load — after the manifest and the
 * worker are read — and on the same devices whose standing answer is
 * `menu-shortcut`. Both are answers to one question and the event is the
 * better one, so the manual answer waits this window out and never arrives at
 * all if the event does. An event that arrives LATER still wins: it overwrites
 * the standing answer the moment it fires.
 */
const PROMPT_ARRIVAL_WINDOW_MS = 3000

/** The current match now, and again on every change. */
const watch = (query: MediaQueryList): Observable<MediaQueryList> =>
	fromEvent(query, 'change').pipe(
		map(() => query),
		startWith(query),
	)

const coarsePointer = window.matchMedia('(pointer: coarse)')
const installedDisplay = window.matchMedia(INSTALLED_DISPLAY_MODES)

/**
 * The deferred offer, held for the page's lifetime.
 *
 * `preventDefault()` is what keeps the event usable — without it Chromium
 * shows its own mini-infobar and there is nothing left for a gesture to raise.
 * That is a real thing taken from the visitor, and the affordance this library
 * renders is what is owed back for it.
 *
 * `shareReplay` rather than `share`: the event fires once, and every later
 * subscriber — the standing answer's own `takeUntil`, on a re-derivation — has
 * to see that it already happened or it will publish a manual instruction over
 * a live prompt.
 *
 * The listener is attached when this module's pipeline is subscribed, which is
 * at import. An event that fired BEFORE that is missed, so a consumer places
 * the affordance in the shell it boots with rather than behind a lazy route.
 */
const deferredPrompt$ = fromEvent<BeforeInstallPromptEvent>(window, 'beforeinstallprompt').pipe(
	tap(offer => offer.preventDefault()),
	shareReplay({ bufferSize: 1, refCount: false }),
)

const requestInstall$ = new Subject<void>()
const dismissInstall$ = new Subject<void>()

/**
 * Raise the browser's own install dialog. Call it from the gesture handler
 * itself — the Subject delivers synchronously into an already-subscribed
 * chain and nothing upstream defers, which is the browser's condition for
 * showing the dialog at all. A no-op on every route but `browser-prompt`.
 */
export function requestInstall(): void {
	requestInstall$.next()
}

/**
 * The visitor turned the offer down. Session-scoped by construction: nothing
 * persists it, so the offer returns on the next visit — the right bargain for
 * a one-line affordance, and the reason there is no `dismissed` flag for a
 * consumer to have to read.
 */
export function dismissInstall(): void {
	dismissInstall$.next()
}

/**
 * Installed, but this page is a tab rather than the app. Answers only when the
 * consumer's manifest lists itself under `related_applications`; an empty
 * answer says nothing, which is why only a non-empty one is read.
 */
const alreadyInstalledElsewhere$: Observable<InstallRoute> = navigator.getInstalledRelatedApps
	? from(navigator.getInstalledRelatedApps()).pipe(
			filter(apps => apps.length > 0),
			map((): InstallRoute => 'installed'),
			// Silently, and only here: the API rejects by design outside a
			// top-level secure context, which every embedded consumer is, and the
			// consequence is that this leg adds nothing — the display-mode query
			// still answers. A warning on every boot inside an iframe would be
			// noise about a documented, benign refusal.
			catchError(() => EMPTY),
		)
	: EMPTY

merge(
	// The browser's own offer, and the two ways it is spent.
	deferredPrompt$.pipe(
		switchMap(offer =>
			merge(
				of<InstallRoute>('browser-prompt'),
				merge(
					requestInstall$.pipe(
						// exhaustMap: a second tap while the dialog is up is the same tap.
						exhaustMap(() =>
							from(offer.prompt()).pipe(
								switchMap(() => offer.userChoice),
								map(choice => choice.outcome === 'dismissed'),
								// Inside the flatten, so a refused raise costs this gesture and
								// never the chain. A deferred offer cannot be raised twice, so
								// an offer that will not raise is an offer already spent.
								catchError((err: unknown) => {
									console.warn('[schmancy-install-app] the deferred install offer could not be raised', err)
									return of(true)
								}),
							),
						),
						filter(Boolean),
					),
					dismissInstall$,
				).pipe(
					// Accepting is `appinstalled`'s to report. A decline retires the
					// offer exactly as our own dismissal does, because the deferred
					// event is spent either way.
					map((): InstallRoute => 'unavailable'),
				),
			),
		),
	),
	// Whether this page IS the installed app, and otherwise the platform's own answer.
	watch(installedDisplay).pipe(
		switchMap(query =>
			query.matches || navigator.standalone === true
				? of<InstallRoute>('installed')
				: watch(coarsePointer).pipe(
						map(
							(pointer): InstallRoute =>
								iosFamily
									? 'ios-share'
									: appleWebKit && safari17Era
										? 'macos-dock'
										: pointer.matches
											? 'menu-shortcut'
											: 'unavailable',
						),
						switchMap(route =>
							route === 'menu-shortcut'
								? timer(PROMPT_ARRIVAL_WINDOW_MS).pipe(
										map(() => route),
										takeUntil(deferredPrompt$),
									)
								: of(route),
						),
					),
		),
	),
	// Installing from an open tab leaves that tab in the browser, so the display
	// mode above stays `browser` and only this event carries the fact.
	fromEvent(window, 'appinstalled').pipe(map((): InstallRoute => 'installed')),
	alreadyInstalledElsewhere$,
)
	.pipe(
		distinctUntilChanged(),
		tap(route => installRoute.set(route)),
	)
	// No teardown, deliberately: which install a browser offers is a fact about
	// the page, so the classification lives as long as the page does. The one
	// subscription is established at import, which is also what arms the
	// `beforeinstallprompt` listener early enough to catch the event.
	.subscribe()
